import React from 'react';
import { Helmet } from 'react-helmet';
import JsonLdList from '../../../common/seo/JsonLdList';
import { SITE_URL, getBreadcrumbSchema, organizationSchema } from '../../../../utils/seo';
import { buildFaqSchema, buildItemListSchema, buildWebPageSchema } from '../../../../utils/schema';
import pageAssets from '../../../../data/pageAssets';

const INDRASON_URL = `${SITE_URL}/brand/indrason`;
const INDRASON_OG_IMAGE = `${SITE_URL}${pageAssets.indrasonBanner}`;
const INDRASON_PRODUCT_IMAGE = `${SITE_URL}${pageAssets.indrasonProduct}`;

const IndrasonSeo = ({ seo, faqItems, applications }) => {
    const schemas = [
        organizationSchema,
        getBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Wires & Cables', url: `${SITE_URL}/wires` },
            { name: 'Indrason Wire', url: INDRASON_URL }
        ]),
        buildWebPageSchema({
            name: seo.webPageName,
            url: INDRASON_URL,
            description: seo.webPageDescription,
            about: [
                { "@type": "Brand", "name": "Indrason" },
                { "@type": "Thing", "name": "Electrical cable", "sameAs": "https://www.wikidata.org/wiki/Q188447" },
                { "@type": "Thing", "name": "Copper", "sameAs": "https://www.wikidata.org/wiki/Q753" }
            ],
            speakable: ['.indrason-section', '.faq-section']
        }),
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Indrason Wire",
            "image": [INDRASON_PRODUCT_IMAGE, INDRASON_OG_IMAGE],
            "description": seo.description,
            "brand": {
                "@type": "Brand",
                "name": "Indrason"
            },
            "manufacturer": {
                "@type": "Organization",
                "name": "NISHAD Cables",
                "url": SITE_URL
            },
            "category": "FRLSH House Wire",
            "additionalProperty": [
                { "@type": "PropertyValue", "name": "Copper Purity", "value": "99.97% pure copper" },
                { "@type": "PropertyValue", "name": "Insulation", "value": "FRLSH" },
                { "@type": "PropertyValue", "name": "Certification", "value": "ISI certified under IS-694" }
            ]
        },
        buildItemListSchema({
            name: 'Indrason Wire Applications',
            items: applications,
            getName: (item) => item.title,
            getUrl: () => INDRASON_URL
        }),
        buildFaqSchema(faqItems)
    ];

    return (
        <>
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                <meta name="keywords" content={seo.keywords} />
                <meta name="subject" content={seo.subject} />
                <meta name="abstract" content={seo.abstract} />
                <meta name="robots" content="index, follow" />
                <meta name="author" content="Nishad Cables" />
                <link rel="canonical" href={INDRASON_URL} />

                <meta name="geo.region" content="IN-RJ" />
                <meta name="geo.placename" content="Alwar" />
                <meta name="geo.position" content="27.5530; 76.6346" />
                <meta name="ICBM" content="27.5530, 76.6346" />

                <meta property="og:type" content="website" />
                <meta property="og:title" content={seo.title} />
                <meta property="og:description" content={seo.ogDescription} />
                <meta property="og:image" content={INDRASON_OG_IMAGE} />
                <meta property="og:image:alt" content="Indrason wire by NISHAD" />
                <meta property="og:url" content={INDRASON_URL} />
                <meta property="og:site_name" content="NISHAD" />
                <meta property="og:locale" content="en_IN" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seo.title} />
                <meta name="twitter:description" content={seo.twitterDescription} />
                <meta name="twitter:image" content={INDRASON_OG_IMAGE} />
            </Helmet>
            <JsonLdList items={schemas} />
        </>
    );
};

export default IndrasonSeo;
